import { LatLng, OSMap, OSMarker, OSRoute, RoutalPalette } from '@nx-smartmonkey/ui';
import { LatLngTuple } from 'leaflet';
import { useMemo } from 'react';
import { Pane } from 'react-leaflet';

import { capacitorService } from '../../../../application/CapacitorService';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { useLocationContext } from '../../context/locationContext/context';
import { useCurrentPosition } from '../../hooks/position/useCurrentPosition';
import { usePermissions } from '../../hooks/usePermissions';
import { useRouteContext } from '../context/context';

type RouteStopsMapProps = {
  height?: string | number;
  selectedRoute: Route;
  handleOnSelectStop: (stop: Stop) => void;
  onLongClick?: (latlng: LatLng) => void;
};

export const RouteStopsMap = ({
  height = `100%`,
  selectedRoute,
  handleOnSelectStop,
  onLongClick,
}: RouteStopsMapProps) => {
  const { routeState } = useRouteContext();
  const { canReorderStops } = usePermissions();
  const { locationState } = useLocationContext();

  let coordsRouteBounds = [
    ...selectedRoute.stops
      .map((point) => (point.location ? ([point.location.lat, point.location.lng] as LatLngTuple) : null))
      .filter((coord): coord is LatLngTuple => coord !== null),
    ...(selectedRoute.start_location
      ? [[selectedRoute.start_location.lat, selectedRoute.start_location.lng] as LatLngTuple]
      : []),
    ...(selectedRoute.end_location
      ? [[selectedRoute.end_location.lat, selectedRoute.end_location.lng] as LatLngTuple]
      : []),
  ];

  // Filter out null and undefined values
  coordsRouteBounds = coordsRouteBounds.filter((coord): coord is LatLngTuple => coord !== null && coord !== undefined);

  const bounds = {
    max: [
      Math.max(...coordsRouteBounds.map((coord) => coord[0])),
      Math.max(...coordsRouteBounds.map((coord) => coord[1])),
    ] as LatLngTuple,
    min: [
      Math.min(...coordsRouteBounds.map((coord) => coord[0])),
      Math.min(...coordsRouteBounds.map((coord) => coord[1])),
    ] as LatLngTuple,
  };

  useCurrentPosition();

  const selectedStop = routeState.selectedStopId ? selectedRoute.getStopById(routeState.selectedStopId) : undefined;

  const oldestReportedStop = selectedRoute.stops.reduce((oldestStop: Stop | undefined, stop) => {
    if (stop.report && (!oldestStop || stop.report.created_at > oldestStop.report!.created_at!)) {
      return stop;
    }
    return oldestStop;
  }, undefined as Stop | undefined);

  const nextStop = selectedRoute.stops.find((stop) => stop.status === `pending`);

  const MarkerRoutes = useMemo(() => {
    return (
      <>
        {selectedRoute.stops.map((stop, idx: number) => {
          if (!stop.location?.isValid()) {
            return null;
          }
          const selected = stop.status === `pending` && stop.id === routeState.selectedStopId;
          const inProgress = routeState.startedReportStop === stop.id;
          const showAlert = !!stop.customer_text_field;
          const showWarning = stop.optimization_warning;

          const marker = (
            <OSMarker
              id={stop.id}
              key={stop.id}
              number={idx + 1}
              done={stop.status === STOP_STATUS.completed}
              incomplete={stop.status === STOP_STATUS.incomplete}
              error={stop.status === STOP_STATUS.canceled}
              lat={stop.location!.lat as number}
              lng={stop.location!.lng as number}
              onClick={() => handleOnSelectStop(stop)}
              showAlert={showAlert}
              showInProgress={inProgress}
              showWarning={showWarning}
              selected={routeState.selectedStopId ? selected : stop.id === nextStop?.id}
              color={RoutalPalette.routeBaseColor}
              kind={stop.type === `pickup` ? `routal_pickup` : `routal_default`}
            />
          );
          if (selected) {
            return (
              <Pane name="selectedStop" style={{ zIndex: 1005 }} key={stop.id}>
                {marker}
              </Pane>
            );
          }
          return marker;
        })}
        {selectedRoute.start_location?.isValid() && (
          <OSMarker
            id={`start-location-${selectedRoute.id}`}
            key={`start-location-${selectedRoute.id}`}
            lat={selectedRoute.start_location.lat!}
            lng={selectedRoute.start_location.lng!}
            kind="start"
          />
        )}
        {selectedRoute.end_location?.isValid() &&
          selectedRoute.end_location.lat !== selectedRoute.start_location?.lat &&
          selectedRoute.end_location.lng !== selectedRoute.start_location?.lng && (
            <OSMarker
              id={`end-location-${selectedRoute.id}`}
              key={`end-location-${selectedRoute.id}`}
              lat={selectedRoute.end_location.lat!}
              lng={selectedRoute.end_location.lng!}
              kind="end"
            />
          )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute.stops, routeState.selectedStopId, routeState.startedReportStop]);

  const contentPosition = useMemo(
    () => (
      <>
        {locationState.currentPosition && (
          <Pane name="currentPosition" style={{ zIndex: 1005 }}>
            <OSMarker
              id={`current-position-${selectedRoute.id}`}
              key={`current-position-${selectedRoute.id}`}
              doNotRefreshBounds
              lat={locationState.currentPosition.lat}
              lng={locationState.currentPosition.lng}
              kind={`routal_truck`}
              color={RoutalPalette.routeBaseColor}
            />
          </Pane>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationState.currentPosition, selectedRoute]
  );
  const contentTrack = useMemo(
    () => (
      <>
        {canReorderStops ? (
          <OSRoute
            id={`driver-${selectedRoute.id}`}
            key={selectedRoute.id}
            path={selectedRoute.planned_track}
            previousStop={{
              lat: oldestReportedStop?.location?.lat ?? selectedRoute.start_location?.lat,
              lng: oldestReportedStop?.location?.lng ?? selectedRoute.start_location?.lng,
            }}
            currentPosition={{ lat: locationState.currentPosition?.lat, lng: locationState.currentPosition?.lng }}
            nextStop={{
              lat: nextStop?.location?.lat ?? selectedRoute.end_location?.lat,
              lng: nextStop?.location?.lng ?? selectedRoute.end_location?.lng,
            }}
          >
            {MarkerRoutes}
          </OSRoute>
        ) : (
          <OSRoute id={`driver-${selectedRoute.id}`} key={selectedRoute.id} path={selectedRoute.planned_track}>
            {MarkerRoutes}
          </OSRoute>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationState.currentPosition, selectedRoute, oldestReportedStop, nextStop, MarkerRoutes]
  );

  return (
    <OSMap
      height={height}
      zoomControl={false}
      touchZoom
      center={
        (selectedStop?.location?.isValid() ? selectedStop.location.toCoordinate().toArray() : undefined) as LatLngTuple
      }
      routeBounds={routeState.selectedStopId ? undefined : [bounds.min, bounds.max]}
      dragging
      centerZoomLevel={14}
      centerOffsetY={-window.innerHeight / 4}
      onLongClick={onLongClick}
      tapHold={capacitorService.isIOS()}
    >
      {contentPosition}
      {contentTrack}
    </OSMap>
  );
};
